import React, { useCallback, useEffect, useState } from 'react';
import { Bell, Send, FlaskConical, Loader2, Users, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${sessionStorage.getItem('admin_token')}`,
});

export default function RaceAlertBlastsCard() {
  const [data, setData] = useState({ cities: [], blasts: [], email_configured: true });
  const [form, setForm] = useState({ city: 'houston', race_date: '', registration_url: '', note: '', event_title: '', discount_code: '' });
  const [sending, setSending] = useState(null);
  const [result, setResult] = useState(null);

  const HYROX50_PRESET = {
    city: 'tampa',
    race_date: 'September 12, 2026',
    event_title: 'Full HYROX Simulation at F45 Training Largo East 🏁',
    registration_url: 'https://www.eventbrite.com/e/1993712798650?aff=oddtdtcreator&utm_source=ninjathlete&utm_medium=email&utm_campaign=race_alert_blast&utm_content=tampa_f45_largo_east_full_hyrox_sim',
    note: 'F45 Training Largo East is running a full HYROX simulation on Saturday, September 12 — every station, race format, real sleds. Lock in your spot on Eventbrite before it fills.',
    discount_code: '',
  };

  const load = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/admin/race-alerts`, { headers: authHeaders() });
      setData(await resp.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const send = async (testOnly) => {
    if (!testOnly && !window.confirm(`Blast ALL ${form.city.toUpperCase()} race-alert subscribers now?`)) return;
    setSending(testOnly ? 'test' : 'blast');
    setResult(null);
    try {
      const resp = await fetch(`${API_URL}/api/admin/race-alerts/blast`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ...form, test_only: testOnly }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.detail || 'Send failed');
      setResult(testOnly ? `Test sent to ${json.recipient}` : `Blast sent — ${json.sent} delivered, ${json.failed} failed`);
      if (!testOnly) load();
    } catch (e) {
      setResult(`Error: ${e.message}`);
    } finally {
      setSending(null);
    }
  };

  const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-teal-500 focus:outline-none';

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="race-alert-blasts-card">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="w-4 h-4 text-amber-500" />
        <h2 className="text-sm font-bold text-gray-900">Race Alert Blasts</h2>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Announce HYROX race dates to city subscribers — branded email with registration link, the city's top 3 gyms (buy-a-class links) and a gear CTA.
      </p>

      <div className="flex flex-wrap gap-3 mb-4">
        {data.cities.map((c) => (
          <div key={c.city} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-100" data-testid={`race-alert-count-${c.city}`}>
            <Users className="w-3 h-3 text-gray-400" />
            <span className="text-xs font-semibold text-gray-700">{c.label}</span>
            <span className="text-xs font-bold text-teal-600">{c.subscribers}</span>
          </div>
        ))}
      </div>

      {!data.email_configured && (
        <p className="text-xs text-red-500 mb-3">Resend email is not configured — blasts will fail.</p>
      )}

      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <select value={form.city} onChange={set('city')} className={`${inputCls} bg-white text-gray-800`} data-testid="blast-city-select">
          {data.cities.map((c) => <option key={c.city} value={c.city}>{c.label} ({c.subscribers} subscribers)</option>)}
        </select>
        <input type="text" placeholder="Race date — e.g. March 14, 2027 *" value={form.race_date} onChange={set('race_date')} className={inputCls} data-testid="blast-race-date-input" />
        <input type="url" placeholder="Registration URL (optional)" value={form.registration_url} onChange={set('registration_url')} className={inputCls} data-testid="blast-registration-url-input" />
        <input type="text" placeholder="Extra note (optional)" value={form.note} onChange={set('note')} className={inputCls} data-testid="blast-note-input" />
        <input type="text" placeholder="Event title (optional — turns this into an event blast)" value={form.event_title} onChange={set('event_title')} className={inputCls} data-testid="blast-event-title-input" />
        <input type="text" placeholder="Discount code (optional — e.g. HYROX25 — 25% off)" value={form.discount_code} onChange={set('discount_code')} className={inputCls} data-testid="blast-discount-code-input" />
      </div>

      <button
        onClick={() => { setForm((f) => ({ ...f, ...HYROX50_PRESET })); setResult(null); }}
        className="mb-3 px-3 py-1.5 rounded-lg bg-lime-100 text-lime-800 text-xs font-semibold hover:bg-lime-200 inline-flex items-center gap-1.5"
        data-testid="blast-preset-hyrox50-btn"
      >
        <Zap className="w-3.5 h-3.5" /> Prefill: Full HYROX Sim · F45 Largo East (Tampa, Sep 12)
      </button>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => send(true)}
          disabled={!!sending || !form.race_date}
          className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-xs font-semibold hover:bg-gray-200 disabled:opacity-50 inline-flex items-center gap-1.5"
          data-testid="blast-test-btn"
        >
          {sending === 'test' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />} Send Test to Admin
        </button>
        <button
          onClick={() => send(false)}
          disabled={!!sending || !form.race_date}
          className="px-3 py-2 rounded-lg bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600 disabled:opacity-50 inline-flex items-center gap-1.5"
          data-testid="blast-send-btn"
        >
          {sending === 'blast' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send Blast
        </button>
        {result && <span className="text-xs font-medium text-gray-600" data-testid="blast-result">{result}</span>}
      </div>

      {data.blasts.length > 0 && (
        <div className="mt-5 pt-4 border-t border-gray-100">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Past blasts</p>
          <div className="space-y-1.5">
            {data.blasts.map((b) => (
              <div key={b.blast_id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-600" data-testid={`blast-history-${b.blast_id}`}>
                <span className="font-semibold text-gray-800">{b.city_label}</span>
                {b.event_title && <span className="text-lime-700 font-medium">{b.event_title}</span>}
                <span>{b.race_date}</span>
                <span className="text-emerald-600 font-medium">{b.sent_count} sent</span>
                {b.failed_count > 0 && <span className="text-red-500">{b.failed_count} failed</span>}
                <span className="text-gray-400">{new Date(b.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
